import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Combobox, MessageBarBody, MessageBarTitle, Option } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';

import { GuideDrawer, GuideDrawerSection } from '../../../theme/components/GuideDrawer';
import { GuideField, GuideFieldGrid } from '../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideTable, GuideTh, guideRowClassName } from '../../../theme/components/GuideTable';
import { StatusPill } from '../../../theme/components/StatusPill';

import {
  commonPatientReference,
  filterPatients,
  findSuggestedPatient,
  filterProtocols,
  getApiErrorMessage,
  hasWarning,
  patientLabel,
  protocolLabel,
} from '../../../functions/ProviderConsumptionImport.Functions';
import { ImportedProviderConsumption, ProviderConsumptionAssignmentRequest } from '../../../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';
import { Protocol } from '../../../models/Entities/Protocols/Protocol';
import { ProtocolPatient } from '../../../models/Entities/Protocols/ProtocolPatient';
import './ProviderConsumptionImport.Styles.css';

/** Cantidad máxima de opciones que se dibujan en cada selector (se filtra escribiendo). */
export const MAX_SELECTOR_OPTIONS = 100;

export interface AssignProtocolPatientDrawerProps {
  open: boolean;
  onDismiss: () => void;
  /** Consumos seleccionados que se pueden asignar (no generados). */
  consumptions: ImportedProviderConsumption[];
  /** Seleccionados que no se incluyen porque ya se generaron. */
  excludedCount?: number;
  LoadProtocols: () => Promise<Protocol[]>;
  LoadPatients: (protocolId: number) => Promise<ProtocolPatient[]>;
  Assign: (request: ProviderConsumptionAssignmentRequest) => Promise<ImportedProviderConsumption[]>;
  /** Asignación guardada (para refrescar la lista). */
  onAssigned?: (updated: ImportedProviderConsumption[]) => void;
}

type LoadState<T> = { status: 'idle' | 'loading' | 'loaded' | 'failed'; data: T[]; error?: unknown };

const idle = <T,>(): LoadState<T> => ({ status: 'idle', data: [] });

/**
 * "Asignar protocolo y paciente" (contrato §6): selector de protocolo ("id - nombre"), selector de paciente
 * del protocolo y, después de guardar, el concepto de contrato resuelto por consumo o la alerta si no hay.
 */
export const AssignProtocolPatientDrawer = (props: AssignProtocolPatientDrawerProps) => {
  const { t } = useTranslation();
  const tr = (key: string, options?: Record<string, unknown>) => t(`providerConsumptionImport.assignDrawer.${key}`, options);

  const [protocols, setProtocols] = useState<LoadState<Protocol>>(idle());
  const [patients, setPatients] = useState<LoadState<ProtocolPatient>>(idle());
  const [protocolId, setProtocolId] = useState<number | undefined>(undefined);
  const [protocolQuery, setProtocolQuery] = useState('');
  const [patientNumber, setPatientNumber] = useState<string | undefined>(undefined);
  const [patientQuery, setPatientQuery] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(undefined);
  const [updated, setUpdated] = useState<ImportedProviderConsumption[] | undefined>(undefined);
  /** Número en protocolo preseleccionado desde la referencia del archivo (sólo sugerencia: nunca se guarda solo). */
  const [suggested, setSuggested] = useState<string | undefined>(undefined);
  const patientsRequest = useRef(0);

  /** Referencia de paciente común a todos los consumos seleccionados (spec 007, US3). */
  const reference = useMemo(() => commonPatientReference(props.consumptions), [props.consumptions]);

  useEffect(() => {
    if (!props.open) return;
    setProtocolId(undefined);
    setProtocolQuery('');
    setPatientNumber(undefined);
    setPatientQuery('');
    setPatients(idle());
    setSuggested(undefined);
    setSubmitted(false);
    setSaveError(undefined);
    setUpdated(undefined);
    let alive = true;
    setProtocols({ status: 'loading', data: [] });
    props
      .LoadProtocols()
      .then((data) => alive && setProtocols({ status: 'loaded', data: data ?? [] }))
      .catch((error: unknown) => alive && setProtocols({ status: 'failed', data: [], error }));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  const selectProtocol = (id: number | undefined) => {
    setProtocolId(id);
    setPatientNumber(undefined);
    setPatientQuery('');
    setSuggested(undefined);
    const requestId = ++patientsRequest.current;
    if (!id) {
      setPatients(idle());
      return;
    }
    setPatients({ status: 'loading', data: [] });
    props
      .LoadPatients(id)
      .then((data) => {
        if (requestId !== patientsRequest.current) return;
        const list = data ?? [];
        setPatients({ status: 'loaded', data: list });
        const match = findSuggestedPatient(list, reference);
        if (match) {
          setPatientNumber(match.numberInProtocol);
          setPatientQuery(patientLabel(match));
          setSuggested(match.numberInProtocol);
        }
      })
      .catch((error: unknown) => requestId === patientsRequest.current && setPatients({ status: 'failed', data: [], error }));
  };

  const protocolOptions = useMemo(() => filterProtocols(protocols.data, protocolQuery).slice(0, MAX_SELECTOR_OPTIONS), [protocols.data, protocolQuery]);
  const patientOptions = useMemo(() => filterPatients(patients.data, patientQuery).slice(0, MAX_SELECTOR_OPTIONS), [patients.data, patientQuery]);
  const selectedPatient = patients.data.find((patient) => patient.numberInProtocol === patientNumber);

  const save = async () => {
    setSubmitted(true);
    if (!protocolId || !patientNumber || props.consumptions.length === 0) return;
    setSaving(true);
    setSaveError(undefined);
    try {
      const patientId = (selectedPatient as (ProtocolPatient & { patientId?: number }) | undefined)?.patientId;
      const request: ProviderConsumptionAssignmentRequest = {
        ids: props.consumptions.map((item) => item.id),
        protocolId,
        patientNumberInProtocol: patientNumber,
      };
      if (typeof patientId === 'number' && patientId > 0) request.patientId = patientId;
      const response = await props.Assign(request);
      setUpdated(response);
      props.onAssigned?.(response);
    } catch (error: unknown) {
      setSaveError(error);
    } finally {
      setSaving(false);
    }
  };

  const protocolError = submitted && !protocolId ? tr('required') : undefined;
  const patientError = submitted && !patientNumber ? tr('required') : undefined;
  const showSuggestion = !!suggested && suggested === patientNumber;

  const patientHint = (): React.ReactNode => {
    if (!protocolId) return tr('patientHint');
    if (patients.status === 'loading') return tr('loadingPatients');
    if (showSuggestion) {
      return (
        <span className="pci-suggestion" data-testid="pci-assign-suggested">
          <StatusPill tone="info">{tr('suggestedPill')}</StatusPill>
          {tr('suggestedFromFile', { reference })}
        </span>
      );
    }
    if (reference && patients.status === 'loaded') {
      return <span data-testid="pci-assign-reference">{tr('fileReference', { reference })}</span>;
    }
    return undefined;
  };

  const renderForm = () => (
    <GuideDrawerSection title={tr('sectionTitle')} description={tr('description')} testId="pci-assign-form">
      {props.excludedCount ? (
        <GuideMessageBar intent="info" layout="multiline" data-testid="pci-assign-excluded">
          <MessageBarBody>{tr('excludedGenerated', { count: props.excludedCount })}</MessageBarBody>
        </GuideMessageBar>
      ) : null}
      {protocols.status === 'failed' && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="pci-assign-protocols-error">
          <MessageBarBody>
            <MessageBarTitle>{tr('loadProtocolsError')}</MessageBarTitle>
            {getApiErrorMessage(protocols.error)}
          </MessageBarBody>
        </GuideMessageBar>
      )}
      {patients.status === 'failed' && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="pci-assign-patients-error">
          <MessageBarBody>
            <MessageBarTitle>{tr('loadPatientsError')}</MessageBarTitle>
            {getApiErrorMessage(patients.error)}
          </MessageBarBody>
        </GuideMessageBar>
      )}
      <GuideFieldGrid>
        <GuideField label={tr('protocol')} required error={protocolError} hint={protocols.status === 'loading' ? tr('loadingProtocols') : undefined}>
          <Combobox
            freeform
            placeholder={tr('protocolPlaceholder')}
            value={protocolQuery}
            selectedOptions={protocolId ? [String(protocolId)] : []}
            disabled={saving || protocols.status === 'loading'}
            onChange={(event) => {
              setProtocolQuery(event.target.value);
              if (protocolId) selectProtocol(undefined);
            }}
            onOptionSelect={(_event, data) => {
              const id = Number(data.optionValue);
              const protocol = protocols.data.find((item) => item.id === id);
              setProtocolQuery(protocolLabel(protocol));
              selectProtocol(protocol ? id : undefined);
            }}
            data-testid="pci-assign-protocol"
          >
            {protocolOptions.length === 0 ? (
              <Option value="" text="" disabled>
                {tr('noProtocols')}
              </Option>
            ) : (
              protocolOptions.map((protocol) => (
                <Option key={protocol.id} value={String(protocol.id)} text={protocolLabel(protocol)}>
                  {protocolLabel(protocol)}
                </Option>
              ))
            )}
          </Combobox>
        </GuideField>
        <GuideField
          label={tr('patient')}
          required
          error={patientError}
          hint={patientHint()}
        >
          <Combobox
            freeform
            placeholder={tr('patientPlaceholder')}
            value={patientQuery}
            selectedOptions={patientNumber ? [patientNumber] : []}
            disabled={saving || !protocolId || patients.status === 'loading'}
            onChange={(event) => {
              setPatientQuery(event.target.value);
              setPatientNumber(undefined);
            }}
            onOptionSelect={(_event, data) => {
              const patient = patients.data.find((item) => item.numberInProtocol === data.optionValue);
              setPatientNumber(patient?.numberInProtocol);
              setPatientQuery(patient ? patientLabel(patient) : '');
            }}
            data-testid="pci-assign-patient"
          >
            {patientOptions.length === 0 ? (
              <Option value="" text="" disabled>
                {tr('noPatients')}
              </Option>
            ) : (
              patientOptions.map((patient) => (
                <Option key={patient.numberInProtocol} value={patient.numberInProtocol} text={patientLabel(patient)}>
                  {patientLabel(patient)}
                </Option>
              ))
            )}
          </Combobox>
        </GuideField>
      </GuideFieldGrid>
    </GuideDrawerSection>
  );

  const renderResult = (items: ImportedProviderConsumption[]) => {
    const withWarning = items.filter(hasWarning).length;
    return (
      <div className="pci-stack" data-testid="pci-assign-result">
        <GuideMessageBar intent={withWarning > 0 ? 'warning' : 'success'} layout="multiline">
          <MessageBarBody>
            <MessageBarTitle>{tr('resultTitle')}</MessageBarTitle>
            {tr('resultText', { withConcept: items.length - withWarning, withWarning })}
          </MessageBarBody>
        </GuideMessageBar>
        <GuideTable ariaLabel={tr('resultTitle')} data-testid="pci-assign-result-table">
          <thead>
            <tr>
              <GuideTh>{t('providerConsumptionImport.columns.code')}</GuideTh>
              <GuideTh>{t('providerConsumptionImport.columns.service')}</GuideTh>
              <GuideTh>{t('providerConsumptionImport.columns.concept')}</GuideTh>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className={guideRowClassName({ pending: hasWarning(item) })}>
                <td className="ichtys-num">{item.medicalServiceCode || '—'}</td>
                <td>{item.serviceName || item.medicalServiceName || '—'}</td>
                <td>
                  {hasWarning(item) ? (
                    <>
                      <StatusPill tone="warning">{t('providerConsumptionImport.alert.noConcept')}</StatusPill>
                      {item.warningsComments && <span className="pci-secondary">{item.warningsComments}</span>}
                    </>
                  ) : (
                    item.conceptDescription || (item.contractConceptId ? `#${item.contractConceptId}` : '—')
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </GuideTable>
      </div>
    );
  };

  return (
    <GuideDrawer
      open={props.open}
      size="medium"
      onDismiss={props.onDismiss}
      preventDismiss={saving}
      closeLabel={tr('close')}
      eyebrow={tr('eyebrow', { count: props.consumptions.length })}
      title={tr('title')}
      testId="pci-assign-drawer"
      secondaryAction={{ label: updated ? tr('close') : tr('cancel') }}
      primaryAction={
        updated
          ? undefined
          : {
              label: tr('save'),
              onClick: save,
              loading: saving,
              disabled: props.consumptions.length === 0,
              testId: 'pci-assign-submit',
            }
      }
    >
      {saveError !== undefined && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="pci-assign-error">
          <MessageBarBody>
            <MessageBarTitle>{tr('saveError')}</MessageBarTitle>
            {getApiErrorMessage(saveError)}
          </MessageBarBody>
        </GuideMessageBar>
      )}
      {updated ? renderResult(updated) : renderForm()}
    </GuideDrawer>
  );
};

export default AssignProtocolPatientDrawer;
